import type { FC } from "react";
import type {
  MatchingJobResult,
  MatchingResultItem,
} from "../../../../hooks/use-free-matching-api";
import { MatchingResultCard } from "./MatchingResultCard";

const TRIGGER_TYPE_LABELS: Record<
  string,
  { label: string; className: string }
> = {
  MANUAL: { label: "手動", className: "badge-primary" },
  AUTO: { label: "自動", className: "badge-info" },
  PRESET: { label: "プリセット", className: "badge-secondary" },
};

export interface MatchingResultsSectionProps {
  job: MatchingJobResult;
  onPropose: (result: MatchingResultItem) => void;
  onCompare: (result: MatchingResultItem) => void;
  onFeedback?: (
    resultId: string,
    rating: number,
    comment?: string | null,
  ) => Promise<void>;
  onAddToPipeline?: (result: MatchingResultItem) => void;
  isAddingToPipeline?: boolean;
}

export const MatchingResultsSection: FC<MatchingResultsSectionProps> = ({
  job,
  onPropose,
  onCompare,
  onFeedback,
  onAddToPipeline,
  isAddingToPipeline,
}) => {
  const sortedResults = [...job.results].sort(
    (a, b) => b.totalScore - a.totalScore,
  );

  const triggerInfo = TRIGGER_TYPE_LABELS[job.triggerType] ?? {
    label: job.triggerType,
    className: "badge-ghost",
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold text-gray-900">
            マッチング結果
          </h3>
          <span className={`badge badge-sm ${triggerInfo.className}`}>
            {triggerInfo.label}
          </span>
        </div>
        <span className="text-xs text-gray-500">
          {job.candidatesSearched}件検索 / {job.resultsSaved}件マッチ
        </span>
      </div>

      {sortedResults.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <p className="text-gray-500">
            マッチする対向メールが見つかりませんでした。
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {sortedResults.map(result => (
            <MatchingResultCard
              key={result.id}
              result={result}
              onPropose={onPropose}
              onCompare={onCompare}
              onFeedback={onFeedback}
              onAddToPipeline={onAddToPipeline}
              isAddingToPipeline={isAddingToPipeline}
            />
          ))}
        </div>
      )}
    </div>
  );
};
